import type { PayrollEntry, Employee } from '../types';
import { parseLocalDateForDisplay, formatLocalDate } from './payrollPeriodCalculator';
import { supabase } from '../lib/supabase';
import { sendEmail } from './mailgunService';

export const sendPayStubEmail = async (entry: PayrollEntry, employee: Employee) => {
  try {
    const payPeriod = `${parseLocalDateForDisplay(entry.payPeriod.start).toLocaleDateString()} - ${parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()}`;
    const subject = `Pay Stub for period ending ${parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()}`;
    const employeeName = `${employee.firstName} ${employee.lastName}`;

    // Calculate YTD totals
    const currentYear = parseLocalDateForDisplay(entry.payPeriod.end).getFullYear();
    const { data: ytdEntries } = await supabase
      .from('payroll_entries')
      .select('*')
      .eq('employee_id', employee.id)
      .gte('pay_period_end', `${currentYear}-01-01`)
      .lte('pay_period_end', entry.payPeriod.end)
      .order('pay_period_end', { ascending: true });

    const ytdTotals = ytdEntries?.reduce((acc, curr) => ({
      grossPay: acc.grossPay + curr.gross_pay,
      netPay: acc.netPay + curr.net_pay,
      totalDeductions: acc.totalDeductions + curr.tax_deduction + curr.insurance_deduction + curr.retirement_deduction
    }), {
      grossPay: 0,
      netPay: 0,
      totalDeductions: 0
    });

    const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;
    const regularPay = entry.grossPay - (entry.bonus || 0);
    const bonus = entry.bonus || 0;
    const totalDeductions = entry.deductions.tax + entry.deductions.insurance + entry.deductions.retirement;

    const emailText = `Dear ${employeeName},

Here is your pay stub for the period ${payPeriod}.

Pay Period: ${formatLocalDate(entry.payPeriod.start)} - ${formatLocalDate(entry.payPeriod.end)}
${entry.payDate ? `Pay Date: ${formatLocalDate(entry.payDate)}` : ''}

EARNINGS
Regular Pay: ${formatCurrency(regularPay)}
${bonus > 0 ? `Bonus: ${formatCurrency(bonus)}` : ''}
Gross Pay: ${formatCurrency(entry.grossPay)}

DEDUCTIONS
Federal Tax: ${formatCurrency(entry.deductions.federalTax)}
Social Security: ${formatCurrency(entry.deductions.socialSecurityTax)}
Medicare: ${formatCurrency(entry.deductions.medicareTax)}
State Tax: ${formatCurrency(entry.deductions.stateTax)}
Insurance: ${formatCurrency(entry.deductions.insurance)}
Retirement: ${formatCurrency(entry.deductions.retirement)}
Total Deductions: ${formatCurrency(totalDeductions)}

NET PAY: ${formatCurrency(entry.netPay)}

YTD TOTALS
Gross Pay YTD: ${formatCurrency(ytdTotals?.grossPay || 0)}
Deductions YTD: ${formatCurrency(ytdTotals?.totalDeductions || 0)}
Net Pay YTD: ${formatCurrency(ytdTotals?.netPay || 0)}

Best regards,
PayrolliPro Team`;

    const emailHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f5f5f5;">
          <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 20px;">
            <tr>
              <td align="center">
                <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                  <!-- Header -->
                  <tr>
                    <td style="background-color: #ff9a01; color: #ffffff; padding: 30px; text-align: center; border-radius: 8px 8px 0 0;">
                      <h1 style="margin: 0; font-size: 24px; font-weight: bold;">Fundi</h1>
                      <p style="margin: 10px 0 0 0; font-size: 16px;">Pay Stub</p>
                    </td>
                  </tr>

                  <!-- Employee Info -->
                  <tr>
                    <td style="padding: 30px;">
                      <p style="margin: 0 0 20px 0; font-size: 16px; color: #333;">Dear ${employeeName},</p>
                      <p style="margin: 0 0 20px 0; font-size: 14px; color: #666;">Here is your pay stub for the period <strong>${payPeriod}</strong>.</p>

                      <!-- Pay Period Box -->
                      <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8fafc; border-radius: 4px; margin-bottom: 20px;">
                        <tr>
                          <td style="padding: 15px;">
                            <p style="margin: 0; font-size: 14px; color: #666;">
                              <strong>Pay Period:</strong> ${formatLocalDate(entry.payPeriod.start)} - ${formatLocalDate(entry.payPeriod.end)}
                            </p>
                            ${entry.payDate ? `<p style="margin: 8px 0 0 0; font-size: 14px; color: #666;">
                              <strong>Pay Date:</strong> ${formatLocalDate(entry.payDate)}
                            </p>` : ''}
                          </td>
                        </tr>
                      </table>

                      <!-- Earnings Section -->
                      <h2 style="margin: 20px 0 10px 0; font-size: 18px; color: #333; border-bottom: 2px solid #ff9a01; padding-bottom: 8px;">Earnings</h2>
                      <table width="100%" cellpadding="8" cellspacing="0" style="margin-bottom: 20px;">
                        <tr>
                          <td style="font-size: 14px; color: #666; padding: 8px 0;">Regular Pay</td>
                          <td align="right" style="font-size: 14px; color: #333; padding: 8px 0;">${formatCurrency(regularPay)}</td>
                        </tr>
                        ${bonus > 0 ? `<tr>
                          <td style="font-size: 14px; color: #16a34a; padding: 8px 0;">Bonus</td>
                          <td align="right" style="font-size: 14px; color: #16a34a; padding: 8px 0;">${formatCurrency(bonus)}</td>
                        </tr>` : ''}
                        <tr style="border-top: 1px solid #e5e7eb;">
                          <td style="font-size: 16px; font-weight: bold; color: #333; padding: 12px 0 8px 0;">Gross Pay</td>
                          <td align="right" style="font-size: 16px; font-weight: bold; color: #333; padding: 12px 0 8px 0;">${formatCurrency(entry.grossPay)}</td>
                        </tr>
                      </table>

                      <!-- Deductions Section -->
                      <h2 style="margin: 20px 0 10px 0; font-size: 18px; color: #333; border-bottom: 2px solid #ff9a01; padding-bottom: 8px;">Deductions</h2>
                      <table width="100%" cellpadding="8" cellspacing="0" style="margin-bottom: 20px;">
                        <tr>
                          <td colspan="2" style="font-size: 14px; font-weight: bold; color: #666; padding: 8px 0;">Tax Deductions</td>
                        </tr>
                        <tr>
                          <td style="font-size: 14px; color: #666; padding: 4px 0 4px 20px;">Federal Tax</td>
                          <td align="right" style="font-size: 14px; color: #333; padding: 4px 0;">${formatCurrency(entry.deductions.federalTax)}</td>
                        </tr>
                        <tr>
                          <td style="font-size: 14px; color: #666; padding: 4px 0 4px 20px;">Social Security</td>
                          <td align="right" style="font-size: 14px; color: #333; padding: 4px 0;">${formatCurrency(entry.deductions.socialSecurityTax)}</td>
                        </tr>
                        <tr>
                          <td style="font-size: 14px; color: #666; padding: 4px 0 4px 20px;">Medicare</td>
                          <td align="right" style="font-size: 14px; color: #333; padding: 4px 0;">${formatCurrency(entry.deductions.medicareTax)}</td>
                        </tr>
                        <tr>
                          <td style="font-size: 14px; color: #666; padding: 4px 0 4px 20px 8px;">State Tax</td>
                          <td align="right" style="font-size: 14px; color: #333; padding: 4px 0 0 0 8px;">${formatCurrency(entry.deductions.stateTax)}</td>
                        </tr>
                        <tr>
                          <td colspan="2" style="font-size: 14px; font-weight: bold; color: #666; padding: 16px 0 8px 0;">Other Deductions</td>
                        </tr>
                        <tr>
                          <td style="font-size: 14px; color: #666; padding: 4px 0 4px 20px;">Insurance</td>
                          <td align="right" style="font-size: 14px; color: #333; padding: 4px 0;">${formatCurrency(entry.deductions.insurance)}</td>
                        </tr>
                        <tr>
                          <td style="font-size: 14px; color: #666; padding: 4px 0 4px 20px;">Retirement</td>
                          <td align="right" style="font-size: 14px; color: #333; padding: 4px 0;">${formatCurrency(entry.deductions.retirement)}</td>
                        </tr>
                        <tr style="border-top: 1px solid #e5e7eb;">
                          <td style="font-size: 14px; font-weight: bold; color: #333; padding: 12px 0 8px 0;">Total Deductions</td>
                          <td align="right" style="font-size: 14px; font-weight: bold; color: #333; padding: 12px 0 8px 0;">${formatCurrency(totalDeductions)}</td>
                        </tr>
                      </table>

                      <!-- Net Pay Section -->
                      <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #16a34a; border-radius: 4px; margin: 20px 0;">
                        <tr>
                          <td style="padding: 20px;">
                            <table width="100%" cellpadding="0" cellspacing="0">
                              <tr>
                                <td style="font-size: 18px; font-weight: bold; color: #ffffff;">NET PAY</td>
                                <td align="right" style="font-size: 24px; font-weight: bold; color: #ffffff;">${formatCurrency(entry.netPay)}</td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </table>

                      <!-- YTD Section -->
                      <h2 style="margin: 20px 0 10px 0; font-size: 18px; color: #333; border-bottom: 2px solid #ff9a01; padding-bottom: 8px;">Year-to-Date Totals</h2>
                      <table width="100%" cellpadding="8" cellspacing="0" style="margin-bottom: 20px;">
                        <tr>
                          <td style="font-size: 14px; color: #666; padding: 8px 0;">Gross Pay YTD</td>
                          <td align="right" style="font-size: 14px; color: #333; padding: 8px 0;">${formatCurrency(ytdTotals?.grossPay || 0)}</td>
                        </tr>
                        <tr>
                          <td style="font-size: 14px; color: #666; padding: 8px 0;">Deductions YTD</td>
                          <td align="right" style="font-size: 14px; color: #333; padding: 8px 0;">${formatCurrency(ytdTotals?.totalDeductions || 0)}</td>
                        </tr>
                        <tr style="border-top: 1px solid #e5e7eb;">
                          <td style="font-size: 14px; font-weight: bold; color: #333; padding: 12px 0 8px 0;">Net Pay YTD</td>
                          <td align="right" style="font-size: 14px; font-weight: bold; color: #333; padding: 12px 0 8px 0;">${formatCurrency(ytdTotals?.netPay || 0)}</td>
                        </tr>
                      </table>

                      <p style="margin: 30px 0 0 0; font-size: 14px; color: #666;">Best regards,<br/><strong>PayrolliPro Team</strong></p>
                    </td>
                  </tr>

                  <!-- Footer -->
                  <tr>
                    <td style="background-color: #f8fafc; padding: 20px; text-align: center; border-radius: 0 0 8px 8px; border-top: 1px solid #e5e7eb;">
                      <p style="margin: 0; font-size: 12px; color: #999;">This is an official pay stub document.</p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </body>
      </html>
    `;

    const result = await sendEmail({
      to: employee.email,
      subject,
      text: emailText,
      html: emailHtml,
    });

    await supabase.rpc('log_email', {
      p_recipient_email: employee.email.trim(),
      p_subject: subject,
      p_status: 'sent',
      p_metadata: {
        employeeId: employee.id,
        payrollEntryId: entry.id,
        payPeriod: {
          start: entry.payPeriod.start,
          end: entry.payPeriod.end
        },
        messageId: result.messageId
      }
    });

    alert('Pay stub has been emailed successfully!');
  } catch (error) {
    console.error('Error sending email:', error);
    const errorMessage = error instanceof Error
      ? error.message
      : 'Failed to send email. Please try again later.';

    await supabase.rpc('log_email', {
      p_recipient_email: employee.email.trim(),
      p_subject: `Pay Stub for period ending ${parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()}`,
      p_status: 'failed',
      p_error_message: errorMessage,
      p_metadata: {
        employeeId: employee.id,
        payrollEntryId: entry.id,
        payPeriod: {
          start: entry.payPeriod.start,
          end: entry.payPeriod.end
        }
      }
    });

    alert(errorMessage.trim() || 'Failed to send email. Please try again.');
    throw new Error(errorMessage);
  }
};